import React, { useEffect, useMemo, useState } from 'react';
import { Download, FileSpreadsheet, Calendar, Building2, MapPin, AlertCircle, Search, TrendingUp, DollarSign, PieChart as PieChartIcon, BarChart2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Legend } from 'recharts';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { DatePicker } from '../components/ui/DatePicker';
import { useOrders } from '../contexts/OrderContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { exportSalesReport } from '../utils/excelExport';
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
export function ReportsPage() {
  useDocumentTitle('Reportes');
  const {
    orders
  } = useOrders();
  const {
    showToast
  } = useToast();
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState('Todas');
  const [selectedBranch, setSelectedBranch] = useState('Todas');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateError, setDateError] = useState('');
  // Applied filters - start showing ALL orders by default
  const [appliedFilters, setAppliedFilters] = useState({
    company: 'Todas',
    branch: 'Todas',
    dateFrom: '',
    dateTo: ''
  });
  // Load companies dynamically from Supabase
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        if (isSupabaseConfigured()) {
          const data = await supabaseClient.getCompanies();
          setCompanies(data);
        } else {
          const saved = localStorage.getItem('criket_companies');
          setCompanies(saved ? JSON.parse(saved) : []);
        }
      } catch (error) {
        console.error('Error loading companies:', error);
        const saved = localStorage.getItem('criket_companies');
        setCompanies(saved ? JSON.parse(saved) : []);
      }
    };
    loadCompanies();
  }, []);
  const companyNames = ['Todas', ...companies.map((c: any) => c.name)];
  const branches = selectedCompany === 'Todas' ? ['Todas'] : ['Todas', ...(companies.find((c: any) => c.name === selectedCompany)?.branches?.map((b: any) => b.name) || [])];
  // Validate date range
  const validateDates = (from: string, to: string) => {
    if (!from || !to) {
      setDateError('');
      return true;
    }
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (fromDate > toDate) {
      setDateError('La fecha "Desde" no puede ser posterior a la fecha "Hasta"');
      return false;
    }
    setDateError('');
    return true;
  };
  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    validateDates(value, dateTo);
  };
  const handleDateToChange = (value: string) => {
    setDateTo(value);
    validateDates(dateFrom, value);
  };
  const handleSearch = () => {
    if (dateError) {
      return;
    }
    setAppliedFilters({
      company: selectedCompany,
      branch: selectedBranch,
      dateFrom: dateFrom,
      dateTo: dateTo
    });
  };
  const filteredOrders = orders.filter(order => {
    // Don't filter cancelled orders
    if (order.status === 'Cancelado') {
      return false;
    }
    // Apply company filter
    if (appliedFilters.company !== 'Todas' && order.company !== appliedFilters.company) {
      return false;
    }
    // Apply branch filter
    if (appliedFilters.branch !== 'Todas' && order.branch !== appliedFilters.branch) {
      return false;
    }
    // Apply date filtering ONLY if dates are provided
    if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      try {
        // Parse order date from Spanish format "DD/MM/YYYY HH:MM:SS AM/PM" or "DD/MM/YYYY, HH:MM:SS AM/PM"
        const cleanDate = order.orderDate.replace(',', '');
        const dateTimeParts = cleanDate.split(' ');
        const datePart = dateTimeParts[0]; // "DD/MM/YYYY"
        const parts = datePart.split('/');
        if (parts.length !== 3) {
          return true;
        }
        const day = parseInt(parts[0]);
        const month = parseInt(parts[1]);
        const year = parseInt(parts[2]);
        // Create date string in YYYY-MM-DD format for comparison (no timezone issues)
        const orderDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        if (appliedFilters.dateFrom) {
          if (orderDateStr < appliedFilters.dateFrom) {
            return false;
          }
        }
        if (appliedFilters.dateTo) {
          if (orderDateStr > appliedFilters.dateTo) {
            return false;
          }
        }
      } catch (e) {
        console.error('❌ Error parsing date:', order.orderDate, e);
        return true;
      }
    }
    return true;
  });
  // Calculate totals
  const totalSales = filteredOrders.reduce((sum, order) => sum + parseFloat(order.total), 0);
  const totalDiscount = filteredOrders.reduce((sum, order) => sum + parseFloat(order.discount || '0'), 0);
  const totalSubsidy = filteredOrders.reduce((sum, order) => sum + parseFloat(order.subsidyAmount || '0'), 0);
  const exemptSales = filteredOrders.filter(o => o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
  const taxableSales = filteredOrders.filter(o => !o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
  const subtotalExempt = exemptSales;
  const subtotalTaxable = taxableSales / 1.18;
  const subtotal = subtotalExempt + subtotalTaxable;
  const tax = taxableSales - subtotalTaxable;
  const totalOrders = filteredOrders.length;
  const averageOrder = totalOrders > 0 ? totalSales / totalOrders : 0;
  const salesByCompany = companies.map(company => {
    const companyOrders = filteredOrders.filter(o => o.company === company.name);
    const total = companyOrders.reduce((sum, o) => sum + parseFloat(o.total), 0);
    const exemptTotal = companyOrders.filter(o => o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
    const taxableTotal = companyOrders.filter(o => !o.taxExempt).reduce((sum, o) => sum + parseFloat(o.total), 0);
    return {
      company: company.name,
      orders: companyOrders.length,
      total: total,
      subtotal: exemptTotal + taxableTotal / 1.18,
      tax: taxableTotal - taxableTotal / 1.18,
      exempt: exemptTotal,
      taxable: taxableTotal
    };
  }).filter(s => s.orders > 0).sort((a, b) => b.total - a.total); // Sort by total sales descending
  const salesByBranch = filteredOrders.reduce((acc, order) => {
    const key = `${order.company} - ${order.branch}`;
    if (!acc[key]) {
      acc[key] = {
        company: order.company,
        branch: order.branch,
        orders: 0,
        total: 0,
        subtotal: 0,
        tax: 0,
        exempt: 0,
        taxable: 0
      };
    }
    const orderTotal = parseFloat(order.total);
    acc[key].orders++;
    acc[key].total += orderTotal;
    if (order.taxExempt) {
      acc[key].exempt += orderTotal;
      acc[key].subtotal += orderTotal;
    } else {
      acc[key].taxable += orderTotal;
      acc[key].subtotal += orderTotal / 1.18;
      acc[key].tax += orderTotal - orderTotal / 1.18;
    }
    return acc;
  }, {} as Record<string, any>);
  const branchSales = Object.values(salesByBranch).sort((a: any, b: any) => b.total - a.total);
  // --- CHART DATA PREPARATION ---
  // 1. Revenue Trend Data (AreaChart)
  const revenueTrendData = useMemo(() => {
    const dataMap: Record<string, {
      date: string;
      ventas: number;
      subsidios: number;
    }> = {};
    filteredOrders.forEach(order => {
      try {
        const cleanDate = order.orderDate.replace(',', '');
        const datePart = cleanDate.split(' ')[0]; // 'DD/MM/YYYY'
        const [day, month, year] = datePart.split('/');
        // Format for sorting/display: YYYY-MM-DD
        const dateKey = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        // Display format: DD/MM
        const displayDate = `${day}/${month}`;
        if (!dataMap[dateKey]) {
          dataMap[dateKey] = {
            date: displayDate,
            ventas: 0,
            subsidios: 0
          };
        }
        dataMap[dateKey].ventas += parseFloat(order.total);
        dataMap[dateKey].subsidios += parseFloat(order.subsidyAmount || '0');
      } catch (e) {
        // ignore parsing errors
      }
    });
    // Sort by date key and return array
    return Object.keys(dataMap).sort().map(key => dataMap[key]);
  }, [filteredOrders]);
  // 2. Top Products Data (PieChart)
  const topProductsData = useMemo(() => {
    const productMap: Record<string, number> = {};
    filteredOrders.forEach(order => {
      const product = order.product || 'Desconocido';
      if (!productMap[product]) {
        productMap[product] = 0;
      }
      productMap[product] += parseFloat(order.total);
    });
    return Object.entries(productMap).map(([name, value]) => ({
      name,
      value
    })).sort((a, b) => b.value - a.value).slice(0, 6); // Top 6 products
  }, [filteredOrders]);
  // 3. Sales by Company Data (BarChart) - reuse salesByCompany but format for chart
  const companyChartData = useMemo(() => {
    return salesByCompany.slice(0, 10).map(c => ({
      name: c.company,
      ventas: c.total
    }));
  }, [salesByCompany]);
  const exportToExcel = () => {
    if (dateError) {
      showToast('warning', 'Error en fechas', 'Corrige el rango de fechas antes de exportar');
      return;
    }
    const orderDetails = filteredOrders.map(order => {
      const orderTotal = parseFloat(order.total);
      const orderSubtotal = order.taxExempt ? orderTotal : orderTotal / 1.18;
      const orderTax = order.taxExempt ? 0 : orderTotal - orderTotal / 1.18;
      const orderSubsidy = parseFloat(order.subsidyAmount || '0');
      return {
        orderNo: order.orderNo,
        orderDate: order.orderDate,
        name: order.name,
        company: order.company,
        branch: order.branch,
        product: order.product,
        quantity: order.quantity,
        subtotal: orderSubtotal,
        tax: orderTax,
        discount: parseFloat(order.discount || '0'),
        subsidy: orderSubsidy,
        taxExempt: order.taxExempt || false,
        total: orderTotal,
        status: order.status
      };
    });
    exportSalesReport({
      filters: {
        company: appliedFilters.company,
        branch: appliedFilters.branch,
        dateFrom: appliedFilters.dateFrom,
        dateTo: appliedFilters.dateTo
      },
      summary: {
        totalOrders,
        subtotal,
        tax,
        totalDiscount,
        totalSubsidy,
        exemptSales,
        taxableSales,
        totalSales,
        averageOrder
      },
      salesByCompany,
      salesByBranch: branchSales.map((s: any) => ({
        company: s.company,
        branch: s.branch,
        orders: s.orders,
        subtotal: s.subtotal,
        tax: s.tax,
        exempt: s.exempt,
        taxable: s.taxable,
        total: s.total
      })),
      orderDetails
    });
    showToast('success', 'Reporte exportado', 'El archivo Excel se ha descargado correctamente');
  };
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white dark:bg-gray-800 p-3 border border-gray-100 dark:border-gray-700 shadow-lg rounded-xl" data-id="element-5336">
          <p className="text-sm font-bold text-gray-900 dark:text-white mb-2" data-id="element-5337">
            {label}
          </p>
          {payload.map((entry: any, index: number) => <p key={index} className="text-xs" style={{
          color: entry.color
        }} data-id="element-5338">
              {entry.name}: RD${' '}
              {entry.value.toLocaleString('es-DO', {
            minimumFractionDigits: 2
          })}
            </p>)}
        </div>;
    }
    return null;
  };
  return <div className="p-6 lg:p-10 max-w-7xl mx-auto space-y-8" data-id="element-5339">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5340">
        <div data-id="element-5341">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight" data-id="element-5342">
            Reportes de Ventas
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-lg" data-id="element-5343">
            Analiza y exporta datos de ventas por empresa y sucursal
          </p>
        </div>
        <Button onClick={exportToExcel} leftIcon={<Download size={18} data-id="element-5345" />} size="lg" disabled={filteredOrders.length === 0 || !!dateError} className="shadow-lg shadow-gray-900/10" data-id="element-5344">
          Exportar a Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="p-0 border-0 shadow-lg shadow-gray-100 dark:shadow-none" data-id="element-5346">
        <div className="px-6 py-4 bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800" data-id="element-5347">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5348">
            <Search size={16} className="text-gray-400" data-id="element-5349" />
            Filtros de Búsqueda
          </h3>
        </div>

        <div className="p-6" data-id="element-5350">
          {dateError && <div className="mb-5 p-3.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3" data-id="element-5351">
              <AlertCircle className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" size={18} data-id="element-5352" />
              <div data-id="element-5353">
                <p className="text-sm font-bold text-red-900 dark:text-red-100" data-id="element-5354">
                  Error en las fechas
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 mt-0.5" data-id="element-5355">
                  {dateError}
                </p>
              </div>
            </div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end" data-id="element-5356">
            <div data-id="element-5357">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5358">
                Empresa
              </label>
              <FilterDropdown value={selectedCompany} options={companyNames} onChange={setSelectedCompany} data-id="element-5359" />
            </div>
            <div data-id="element-5360">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5361">
                Sucursal
              </label>
              <FilterDropdown value={selectedBranch} options={branches} onChange={setSelectedBranch} data-id="element-5362" />
            </div>
            <div data-id="element-5363">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5364">
                Desde
              </label>
              <DatePicker value={dateFrom} onChange={handleDateFromChange} placeholder="Fecha inicio" data-id="element-5365" />
            </div>
            <div data-id="element-5366">
              <label className="block text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 uppercase tracking-wider" data-id="element-5367">
                Hasta
              </label>
              <DatePicker value={dateTo} onChange={handleDateToChange} placeholder="Fecha fin" data-id="element-5368" />
            </div>
            <div data-id="element-5369">
              <Button onClick={handleSearch} leftIcon={<Search size={16} data-id="element-5371" />} disabled={!!dateError} fullWidth className="h-10" data-id="element-5370">
                Aplicar Filtros
              </Button>
            </div>
          </div>

          {/* Active filters indicator */}
          {(appliedFilters.company !== 'Todas' || appliedFilters.branch !== 'Todas' || appliedFilters.dateFrom || appliedFilters.dateTo) && <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2" data-id="element-5372">
              <span className="text-[11px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider" data-id="element-5373">
                Filtros activos:
              </span>
              {appliedFilters.company !== 'Todas' && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5374">
                  <Building2 size={12} data-id="element-5375" /> {appliedFilters.company}
                </span>}
              {appliedFilters.branch !== 'Todas' && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5376">
                  <MapPin size={12} data-id="element-5377" /> {appliedFilters.branch}
                </span>}
              {appliedFilters.dateFrom && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5378">
                  <Calendar size={12} data-id="element-5379" /> Desde: {appliedFilters.dateFrom}
                </span>}
              {appliedFilters.dateTo && <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-md text-xs font-medium" data-id="element-5380">
                  <Calendar size={12} data-id="element-5381" /> Hasta: {appliedFilters.dateTo}
                </span>}
              <button onClick={() => {
            setSelectedCompany('Todas');
            setSelectedBranch('Todas');
            setDateFrom('');
            setDateTo('');
            setDateError('');
            setAppliedFilters({
              company: 'Todas',
              branch: 'Todas',
              dateFrom: '',
              dateTo: ''
            });
          }} className="text-xs text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 font-medium underline underline-offset-2 transition-colors ml-1" data-id="element-5382">
                Limpiar todo
              </button>
            </div>}
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-5383">
        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-blue-500 group hover:shadow-md transition-shadow" data-id="element-5384">
          <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5385">
            <FileSpreadsheet className="w-6 h-6 text-blue-600 dark:text-blue-400" data-id="element-5386" />
          </div>
          <div data-id="element-5387">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5388">
              Total Pedidos
            </p>
            <div className="flex items-baseline gap-2" data-id="element-5389">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5390">
                {totalOrders}
              </p>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded flex items-center" data-id="element-5391">
                <ArrowUpRight size={10} className="mr-0.5" data-id="element-5392" /> +12%
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-green-500 group hover:shadow-md transition-shadow" data-id="element-5393">
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5394">
            <DollarSign className="w-6 h-6 text-green-600 dark:text-green-400" data-id="element-5395" />
          </div>
          <div data-id="element-5396">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5397">
              Ventas Totales
            </p>
            <div className="flex items-baseline gap-2" data-id="element-5398">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5399">
                RD${' '}
                {totalSales.toLocaleString('es-DO', {
                minimumFractionDigits: 2
              })}
              </p>
              <span className="text-xs font-medium text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-1.5 py-0.5 rounded flex items-center" data-id="element-5400">
                <ArrowUpRight size={10} className="mr-0.5" data-id="element-5401" /> +8%
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-purple-500 group hover:shadow-md transition-shadow" data-id="element-5402">
          <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5403">
            <PieChartIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-5404" />
          </div>
          <div data-id="element-5405">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5406">
              ITBIS (18%)
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5407">
              RD${' '}
              {tax.toLocaleString('es-DO', {
              minimumFractionDigits: 2
            })}
            </p>
          </div>
        </Card>

        <Card className="p-5 flex items-center gap-4 border-l-4 border-l-orange-500 group hover:shadow-md transition-shadow" data-id="element-5408">
          <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl group-hover:scale-110 transition-transform" data-id="element-5409">
            <TrendingUp className="w-6 h-6 text-orange-600 dark:text-orange-400" data-id="element-5410" />
          </div>
          <div data-id="element-5411">
            <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-5412">
              Ticket Promedio
            </p>
            <div className="flex items-baseline gap-2" data-id="element-5413">
              <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-5414">
                RD${' '}
                {averageOrder.toLocaleString('es-DO', {
                minimumFractionDigits: 2
              })}
              </p>
              <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded flex items-center" data-id="element-5415">
                -
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* CHARTS SECTION */}
      {filteredOrders.length > 0 && <div className="space-y-6" data-id="element-5416">
          <div className="flex items-center gap-2 mb-2" data-id="element-5417">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg" data-id="element-5418">
              <BarChart2 size={20} className="text-indigo-600 dark:text-indigo-400" data-id="element-5419" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-5420">
              Análisis Visual
            </h2>
          </div>

          {/* Revenue Trend Chart */}
          <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm" data-id="element-5421">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5422">
              Tendencia de Ingresos
            </h3>
            <div className="h-[300px] w-full" data-id="element-5423">
              <ResponsiveContainer width="100%" height="100%" data-id="element-5424">
                <AreaChart data={revenueTrendData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} data-id="element-5425">
                  <defs data-id="element-5426">
                    <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1" data-id="element-5427">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} data-id="element-5428" />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} data-id="element-5429" />
                    </linearGradient>
                    <linearGradient id="colorSubsidios" x1="0" y1="0" x2="0" y2="1" data-id="element-5430">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} data-id="element-5431" />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-5432" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-5433" />
                  <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} data-id="element-5434" />
                  <YAxis stroke="#9CA3AF" fontSize={12} tickLine={false} axisLine={false} tickFormatter={value => `RD$${value / 1000}k`} data-id="element-5435" />
                  <Tooltip content={<CustomTooltip data-id="element-5437" />} data-id="element-5436" />
                  <Area type="monotone" dataKey="ventas" name="Ventas Totales" stroke="#3b82f6" fillOpacity={1} fill="url(#colorVentas)" strokeWidth={2} data-id="element-5438" />
                  <Area type="monotone" dataKey="subsidios" name="Subsidios" stroke="#10b981" fillOpacity={1} fill="url(#colorSubsidios)" strokeWidth={2} data-id="element-5439" />
                  <Legend data-id="element-5440" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5441">
            {/* Sales by Company Chart */}
            <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm flex flex-col" data-id="element-5442">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5443">
                Ventas por Empresa
              </h3>
              <div className="h-[300px] w-full flex-1" data-id="element-5444">
                <ResponsiveContainer width="100%" height="100%" data-id="element-5445">
                  <BarChart layout="vertical" data={companyChartData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-5446">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" data-id="element-5447" />
                    <XAxis type="number" hide data-id="element-5448" />
                    <YAxis dataKey="name" type="category" width={100} stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} data-id="element-5449" />
                    <Tooltip content={<CustomTooltip data-id="element-5451" />} data-id="element-5450" />
                    <Bar dataKey="ventas" name="Ventas" fill="#6366f1" radius={[0, 4, 4, 0]} barSize={20} data-id="element-5452" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Top Products Chart */}
            <Card className="p-6 border border-gray-200 dark:border-gray-800 shadow-sm flex flex-col" data-id="element-5453">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5454">
                Productos Más Vendidos
              </h3>
              <div className="h-[300px] w-full flex-1" data-id="element-5455">
                <ResponsiveContainer width="100%" height="100%" data-id="element-5456">
                  <PieChart data-id="element-5457">
                    <Pie data={topProductsData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" paddingAngle={5} dataKey="value" data-id="element-5458">
                      {topProductsData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-5459" />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip data-id="element-5461" />} data-id="element-5460" />
                    <Legend verticalAlign="bottom" height={36} iconType="circle" formatter={value => <span className="text-xs text-gray-600 dark:text-gray-300 ml-1" data-id="element-5463">
                          {value}
                        </span>} data-id="element-5462" />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          </div>
        </div>}

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-5464">
        {/* Sales by Company Table (Legacy View) */}
        <Card className="h-full flex flex-col" data-id="element-5465">
          <div className="flex items-center justify-between mb-6" data-id="element-5466">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5467">
              <Building2 className="w-5 h-5 text-gray-400" data-id="element-5468" />
              Detalle por Empresa
            </h3>
          </div>
          <div className="space-y-4 flex-1 overflow-y-auto max-h-[400px] pr-2" data-id="element-5469">
            {salesByCompany.map((sale, index) => <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-5470">
                <div className="flex justify-between items-start mb-2" data-id="element-5471">
                  <div data-id="element-5472">
                    <p className="font-bold text-gray-900 dark:text-white" data-id="element-5473">
                      {sale.company}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5474">
                      {sale.orders} pedidos
                    </p>
                  </div>
                  <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5475">
                    RD${' '}
                    {sale.total.toLocaleString('es-DO', {
                  minimumFractionDigits: 2
                })}
                  </p>
                </div>

                {/* Visual bar */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mb-2" data-id="element-5476">
                  <div className="bg-blue-600 h-1.5 rounded-full" style={{
                width: `${sale.total / totalSales * 100}%`
              }} data-id="element-5477"></div>
                </div>

                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400" data-id="element-5478">
                  <span data-id="element-5479">
                    Subtotal: RD${' '}
                    {sale.subtotal.toLocaleString('es-DO', {
                  minimumFractionDigits: 2
                })}
                  </span>
                  <span data-id="element-5480">
                    ITBIS: RD${' '}
                    {sale.tax.toLocaleString('es-DO', {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>
              </div>)}
            {salesByCompany.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400 italic" data-id="element-5481">
                No hay datos para mostrar
              </div>}
          </div>
        </Card>

        {/* Sales by Branch */}
        <Card className="h-full flex flex-col" data-id="element-5482">
          <div className="flex items-center justify-between mb-6" data-id="element-5483">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-5484">
              <MapPin className="w-5 h-5 text-gray-400" data-id="element-5485" />
              Ventas por Sucursal
            </h3>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto flex-1" data-id="element-5486">
            <table className="w-full text-left border-collapse" data-id="element-5487">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0" data-id="element-5488">
                <tr data-id="element-5489">
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-5490">
                    Sucursal
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-5491">
                    Pedidos
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right" data-id="element-5492">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-5493">
                {branchSales.map((sale: any, index: number) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-5494">
                    <td className="px-4 py-3" data-id="element-5495">
                      <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-5496">
                        {sale.branch}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5497">
                        {sale.company}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-right font-mono" data-id="element-5498">
                      {sale.orders}
                    </td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-right" data-id="element-5499">
                      RD${' '}
                      {sale.total.toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                    </td>
                  </tr>)}
                {branchSales.length === 0 && <tr data-id="element-5500">
                    <td colSpan={3} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 italic" data-id="element-5501">
                      No hay datos para mostrar
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3 flex-1 overflow-y-auto max-h-[400px]" data-id="element-5502">
            {branchSales.map((sale: any, index: number) => <div key={index} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-5503">
                <div className="flex justify-between items-start mb-2" data-id="element-5504">
                  <div data-id="element-5505">
                    <p className="font-bold text-gray-900 dark:text-white" data-id="element-5506">
                      {sale.branch}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-5507">
                      {sale.company}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-5508">
                    <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-5509">
                      RD${' '}
                      {sale.total.toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono" data-id="element-5510">
                      {sale.orders} pedidos
                    </p>
                  </div>
                </div>

                {/* Mini bar chart visual */}
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 mt-2" data-id="element-5511">
                  <div className="bg-purple-600 h-1 rounded-full" style={{
                width: `${Math.min(sale.total / (branchSales[0]?.total || 1) * 100, 100)}%`
              }} data-id="element-5512"></div>
                </div>
              </div>)}
            {branchSales.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400 italic" data-id="element-5513">
                No hay datos para mostrar
              </div>}
          </div>
        </Card>
      </div>
    </div>;
}